import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  createPriceAgreementAsync,
  CreatePriceAgreementParams,
  deletePriceAgreementAsync,
  fetchPriceAgreementAsync,
  fetchPriceAgreementsAsync,
  fetchPriceAgreementsForDivisionAsync,
  fetchPriceAgreementsForSupplierAsync,
  FetchPriceAgreementsParams,
  getPriceAgreementSummary,
  UpdatePriceAgreementParams,
  updatePriceAgreementAsync,
} from '@api/AgreementsApi';
import { ID } from 'types/ID';

export const PRICE_AGREEMENTS_QUERY_KEY = 'priceAgreements';
export const PRICE_AGREEMENT_SUMMARY_QUERY_KEY = 'priceAgreementSummary';

export const usePriceAgreements = (params: FetchPriceAgreementsParams, options: { enabled?: boolean } = {}) => {
  return useQuery({
    enabled: options.enabled ?? true,
    placeholderData: keepPreviousData,
    queryFn: async () => {
      const response = await fetchPriceAgreementsAsync(params);

      return response.data;
    },
    queryKey: [PRICE_AGREEMENTS_QUERY_KEY, params],
  });
};

export const useCreatePriceAgreement = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (params: CreatePriceAgreementParams) => createPriceAgreementAsync(params),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: [PRICE_AGREEMENTS_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: [PRICE_AGREEMENT_SUMMARY_QUERY_KEY] });
    },
  });
};

export const usePriceAgreement = (id: number | string | null | undefined) => {
  return useQuery({
    enabled: !!id,
    queryFn: async () => {
      const response = await fetchPriceAgreementAsync(id);

      return response.data.price_agreement;
    },
    queryKey: [PRICE_AGREEMENTS_QUERY_KEY, id],
  });
};

export const usePriceAgreementSummary = () => {
  return useQuery({
    queryFn: async () => {
      const response = await getPriceAgreementSummary();

      return response.data.summary;
    },
    queryKey: [PRICE_AGREEMENT_SUMMARY_QUERY_KEY],
  });
};

export const useUpdatePriceAgreement = (id: ID) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (params: UpdatePriceAgreementParams) => updatePriceAgreementAsync(id, params),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: [PRICE_AGREEMENTS_QUERY_KEY] });
    },
  });
};

export const useDeletePriceAgreement = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: ID) => deletePriceAgreementAsync(id),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: [PRICE_AGREEMENTS_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: [PRICE_AGREEMENT_SUMMARY_QUERY_KEY] });
    },
  });
};

export const usePriceAgreementsForSupplier = (supplierId: number | string) => {
  return useQuery({
    enabled: !!supplierId,
    queryFn: async () => {
      const response = await fetchPriceAgreementsForSupplierAsync(supplierId);

      return response.data;
    },
    queryKey: [PRICE_AGREEMENTS_QUERY_KEY, 'supplier', supplierId],
  });
};

export const usePriceAgreementsForDivision = (divisionId: number | string) => {
  return useQuery({
    enabled: !!divisionId,
    queryFn: async () => {
      const response = await fetchPriceAgreementsForDivisionAsync(divisionId);

      return response.data;
    },
    queryKey: [PRICE_AGREEMENTS_QUERY_KEY, 'division', divisionId],
  });
};
